import {
  Camera, Siren, Fingerprint, Wifi,
  Flame, FireExtinguisher, Droplets, Waves, DoorClosed,
  Gauge, Cylinder, Lightbulb, Fan, CloudFog, BellRing,
  Shield, Factory,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Mapa central de iconos por tipo de sistema.
 * Claves canónicas en `systemTypeConstants.ts`; se incluyen también claves
 * legacy usadas en algunos formularios y catálogos para retrocompatibilidad.
 */
const SYSTEM_TYPE_ICON_MAP: Record<string, LucideIcon> = {
  // ===== Seguridad =====
  CCTV: Camera,
  'intrusión': Siren,
  control_accesos: Fingerprint,
  networking: Wifi,

  // ===== PCI (claves canónicas) =====
  pci_deteccion_incendios: BellRing,
  extintores: FireExtinguisher,
  bies_hidrantes: Droplets,
  pci_deteccion_co: CloudFog,
  pci_deteccion_gases: CloudFog,
  exutorios: Fan,
  pci_tuberia: Waves,
  pci_puertas_cortafuegos: DoorClosed,
  pci_grupo_presion: Gauge,
  pci_extincion_gaseosa: Cylinder,
  pci_alumbrado_senalizacion: Lightbulb,

  // ===== Claves legacy (compatibilidad) =====
  accesos: Fingerprint,
  deteccion_alarma: BellRing,
  rociadores: Droplets,
  sistemas_gases: Cylinder,
  puertas_rf: DoorClosed,
  monoxido_carbono: CloudFog,
  grupos_presion: Gauge,
};

/** Devuelve el componente de icono Lucide para un tipo de sistema. */
export const getSystemTypeIcon = (tipo: string): LucideIcon =>
  SYSTEM_TYPE_ICON_MAP[tipo] || Factory;

/** Devuelve el icono de la categoría (seguridad / PCI). */
export const getCategoryIcon = (categoria: 'seguridad' | 'pci'): LucideIcon =>
  categoria === 'seguridad' ? Shield : Flame;

/**
 * Color de fondo (clase Tailwind) para el badge/avatar del tipo de sistema.
 * Tonos elegidos para ser distinguibles y coherentes con la semántica.
 */
const SYSTEM_TYPE_COLOR_MAP: Record<string, string> = {
  // Seguridad
  CCTV: 'bg-blue-500',
  'intrusión': 'bg-red-500',
  control_accesos: 'bg-green-500',
  accesos: 'bg-green-500',
  networking: 'bg-purple-500',

  // PCI
  pci_deteccion_incendios: 'bg-orange-600',
  deteccion_alarma: 'bg-orange-600',
  extintores: 'bg-red-600',
  bies_hidrantes: 'bg-blue-600',
  rociadores: 'bg-cyan-600',
  pci_deteccion_co: 'bg-slate-600',
  monoxido_carbono: 'bg-slate-600',
  pci_deteccion_gases: 'bg-purple-600',
  sistemas_gases: 'bg-purple-600',
  exutorios: 'bg-indigo-600',
  pci_tuberia: 'bg-sky-600',
  pci_puertas_cortafuegos: 'bg-yellow-600',
  puertas_rf: 'bg-yellow-600',
  pci_grupo_presion: 'bg-emerald-600',
  grupos_presion: 'bg-emerald-600',
  pci_extincion_gaseosa: 'bg-fuchsia-600',
  pci_alumbrado_senalizacion: 'bg-amber-500',
};

export const getSystemTypeColor = (tipo: string, categoria?: 'seguridad' | 'pci'): string =>
  SYSTEM_TYPE_COLOR_MAP[tipo] || (categoria === 'pci' ? 'bg-red-500' : 'bg-gray-500');
